/**
 * Cosmos DB container and partition key strategy.
 *
 * FIVE containers (see docs/architecture/data-model.md and RD-09 in
 * reference-document-decisions.md for the full rationale):
 *
 * | container      | partition key       | contains                                        |
 * |----------------|---------------------|-------------------------------------------------|
 * | `core`         | `/pk`               | user, customer, setting, integrationConfiguration, documentType |
 * | `customerdata` | `/pk` (=customerId) | project, locationNode, technicalObject, measurementPoint, measurement, asset |
 * | `documents`    | `/pk` (=customerId) | document, documentVersion, documentSection       |
 * | `sharelinks`   | `/pk` (=tokenHash)  | shareLink (point reads only)                     |
 * | `audit`        | `/pk` (=scope)      | auditEvent (TTL enabled)                         |
 *
 * Why not one container per entity: Cosmos bills per container in provisioned
 * mode and every extra container adds operational surface for zero benefit at
 * our data volume. Grouping by ACCESS PATTERN instead of by entity keeps
 * queries single-partition and cheap.
 *
 * Why `customerdata` and `documents` are separate although both are partitioned
 * by `customerId`: master data is read by every draft, document sections only
 * by their own document. A shared partition would drag one into every read of
 * the other.
 *
 * The customer scoped containers use the customer id as partition key on
 * purpose: it makes "everything about one customer" a single-partition query
 * AND turns the partition key into a hard tenant boundary that every repository
 * must pass explicitly.
 */
export const CONTAINERS = {
    core: 'core',
    customerData: 'customerdata',
    documents: 'documents',
    shareLinks: 'sharelinks',
    audit: 'audit',
    clarifications: 'clarifications',
};
/** The only organisation for now. Kept explicit so multi-org stays possible. */
export const DEFAULT_ORGANIZATION_ID = 'oscom';
/** Partition key builders for the `core` container. */
export const corePartition = {
    users: (organizationId) => `org::${organizationId}::users`,
    customers: (organizationId) => `org::${organizationId}::customers`,
    config: (organizationId) => `org::${organizationId}::config`,
    documentTypes: (organizationId) => `org::${organizationId}::documenttypes`,
};
/**
 * Partition key for the `clarifications` container.
 *
 * Its own container on purpose: the export pipeline reads these answers, and a
 * Cosmos role assignment cannot be scoped below container level. Sharing `core`
 * would grant that pipeline read access to customer master data too.
 */
export const clarificationsPartition = {
    answers: (organizationId) => `org::${organizationId}::answers`,
};
/**
 * Partition key for the customer scoped containers (`customerdata`,
 * `documents`): everything is customer scoped, always.
 */
export function customerPartition(customerId) {
    return customerId;
}
/** Partition key for the `sharelinks` container: the token hash itself. */
export function shareLinkPartition(tokenHash) {
    return tokenHash;
}
/**
 * Partition key for the `audit` container.
 * Customer related events are grouped per customer, platform events per month
 * so that neither partition grows without bound.
 */
export const auditPartition = {
    customer: (customerId) => `customer::${customerId}`,
    /** `yearMonth` must be `YYYY-MM` (UTC). */
    platform: (yearMonth) => `platform::${yearMonth}`,
};
/** Stable, readable ids. `crypto.randomUUID()` supplies the random part. */
export const entityId = {
    customer: (uuid) => `cus_${uuid}`,
    project: (uuid) => `prj_${uuid}`,
    location: (uuid) => `loc_${uuid}`,
    technicalObject: (uuid) => `obj_${uuid}`,
    measurementPoint: (uuid) => `mpt_${uuid}`,
    measurement: (uuid) => `mes_${uuid}`,
    asset: (uuid) => `ast_${uuid}`,
    documentType: (key) => `dtp_${key}`,
    document: (uuid) => `doc_${uuid}`,
    documentVersion: (documentId, versionNumber) => `${documentId}_v${String(versionNumber).padStart(4, '0')}`,
    documentSection: (uuid) => `sec_${uuid}`,
    auditEvent: (uuid) => `aud_${uuid}`,
    /** Users are keyed by their Entra ID object id - stable and never reused. */
    user: (entraObjectId) => `usr_${entraObjectId}`,
};
//# sourceMappingURL=partitioning.js.map